import React, { cloneElement, useCallback, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { Check, Loader2, X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { SearchBtnIcon } from '@/components/agent-dashboard/assets';
import { TextInput } from '@/components/common/text-input';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import { agentCompanyAssetHooks } from '@/manager';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { AdminTemplateListItemData } from '@/services/admin-article-template-list';
import { RadioButton } from '../common/radio-button';

interface AdminArticleTemplateModalProps {
  trigger?: React.ReactElement;
  agentId?: string;
  open?: boolean;
  saving?: boolean;
  showPresetTag?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave?: (data: AdminTemplateListItemData[]) => Promise<void>;
}

/** 后台文章模板弹窗 */
export const AdminArticleTemplateModal: React.FC<AdminArticleTemplateModalProps> = ({
  trigger,
  agentId,
  open: externalOpen,
  saving,
  showPresetTag,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const {
    loading,
    loadingMore,
    adminArticleTemplates,
    adminArticleTemplatesSearchForm,
    changeSearchForm,
    search,
    loadMore,
  } = agentCompanyAssetHooks.useAdminArticleTemplatesData(agentId);

  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const isTriggerMode = !!trigger;
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    if (!open) {
      setSelectedRowIds([]);
      setSelectAll(false);
    }
  };

  const handleSearch = async () => {
    changeSearchForm("page", 1);
    await search();
    setSelectedRowIds([]);
  };

  const handleItemSelect = (rowId: string) => {
    setSelectedRowIds((prev) => {
      if (prev.includes(rowId)) {
        return prev.filter((id) => id !== rowId);
      }
      return [...prev, rowId];
    });
  };

  const handleSave = async () => {
    if (!selectedRowIds.length) return;

    await onSave?.(
      selectedRowIds
        .map((id) => adminArticleTemplates.find((item) => item.id === id))
        .filter((item): item is AdminTemplateListItemData => !!item)
    );
    handleClose();
  };

  const handleClose = () => {
    handleOpenChange(false);
  };

  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  useEffect(() => {
    if (selectAll && adminArticleTemplates.length > 0) {
      const ids = adminArticleTemplates.map((item) => item.id);
      setSelectedRowIds(ids);
    } else if (!selectAll) {
      setSelectedRowIds([]);
    }
  }, [selectAll]);

  useEffect(() => {
    if (!currentOpen) return;

    setTimeout(() => {
      titleRef.current?.focus();
    }, 0);

    changeSearchForm('templateId', '');
    changeSearchForm('page', 1);
    void search();
  }, [currentOpen]);

  const handleScrollAreaScroll = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el || loading || loadingMore) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight < scrollHeight - 100) return;
    const total = adminArticleTemplatesSearchForm.total;
    if (total > 0 && adminArticleTemplates.length >= total) return;
    void loadMore();
  }, [
    adminArticleTemplates.length,
    adminArticleTemplatesSearchForm.total,
    loadMore,
    loading,
    loadingMore,
  ]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[750px] h-[755px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6  border-b border-[rgba(233,234,235,1)] pb-5">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-medium flex items-center'
          >
            <span>{t('agent_dashboard.company_asset.choose_template')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        <div className='flex-1 flex flex-col px-6 pt-5 select-none overflow-hidden'>
          <div className='flex items-center gap-4 mb-5'>
            <TextInput
              type="text"
              placeholder={t('agent_dashboard.user_detail.template.all_style_modal.search_placeholder')}
              value={adminArticleTemplatesSearchForm?.templateId}
              className='h-10 !font-normal'
              onChange={(e) => changeSearchForm('templateId', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void handleSearch();
                }
              }}
            />
            <TextButton
              label={t('agent_dashboard.user_detail.template.all_style_modal.search')}
              icon={<SearchBtnIcon className='w-5 h-5' />}
              className={cn(
                '!w-[106px] h-9 ',
                'text-white font-semibold text-sm',
              )}
              onClick={() => void handleSearch()}
            />
          </div>
          <div className='w-full h-full relative overflow-hidden'>
            <div
              ref={scrollAreaRef}
              onScroll={handleScrollAreaScroll}
              className='pt-0.5 pl-0.5 flex-1 flex flex-col h-full overflow-y-auto'
            >
              <div className='grid grid-cols-6 gap-x-3 gap-y-5 pb-5'>
                {adminArticleTemplates?.map((item) => (
                  <div key={item.id} className="w-full h-full">
                    <div
                      className={cn(
                        "w-[102px] h-[136px] group relative rounded-[12px] transition-all cursor-pointer p-0.5 overflow-hidden select-none",
                        selectedRowIds.includes(item.id)
                          ? "outline outline-2 outline-i-normal"
                          : "outline outline-transparent"
                      )}
                      onClick={() => handleItemSelect(item.id)}
                    >
                      <div className='relative size-full overflow-hidden flex-shrink-0'>
                        <Image src={item.coverUrl} key={item.id} className="size-full rounded-[12px] object-cover border border-[rgba(208,213,221,1)]" />
                        {showPresetTag && item.templateResource === 1 && <span className='absolute top-2 left-2 bg-black/40 text-[rgba(255,255,255,1)] text-xs font-medium rounded-[4px] px-1 py-0.5'>{t('agent_dashboard.user_detail.template.add_modal.preset')}</span>}
                      </div>
                      <div
                        className={cn(
                          "size-11 bg-black  absolute -bottom-5 -right-5 opacity-0 transition-all duration-150 rounded-tl-[18.4px] rounded-br-[10px] rounded-tr-none rounded-bl-none",
                          selectedRowIds.includes(item.id) && "opacity-100"
                        )}
                      />
                      <Check className={cn(
                        "size-4 text-white absolute bottom-0.5 right-1 font-bold opacity-0",
                        selectedRowIds.includes(item.id) && "opacity-100"
                      )} />
                    </div>
                    <div className="w-full text-center mt-3">
                      <div className="w-full">
                        <TruncatedText text={item.templateName} className='max-w-[90%] text-sm text-[#333333]' />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {loadingMore && (
                <div className="flex justify-center items-center py-3">
                  <Loader2 className="size-5 animate-spin text-[rgba(71,84,103,1)]" aria-hidden />
                </div>
              )}
              <LoadingOverlay loading={(loading || !!saving) && !loadingMore} />
            </div>
          </div>
        </div>
        <div className='w-full h-[93px] flex justify-between p-6 gap-2 border-t border-[rgba(233,234,235,1)]'>
          <div className='flex items-center gap-2'>
            <RadioButton
              label={t('agent_dashboard.user_detail.larkImageTemplate.add_modal.all_selecting')}
              checked={selectAll}
              onChange={setSelectAll}
              disabled={loading || !!saving || !adminArticleTemplates?.length}
            />
            <div className='text-xs text-[rgba(71,84,103,1)]'>
              {t('agent_dashboard.user_detail.larkImageTemplate.add_modal.selected')} {selectedRowIds.length}
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <TextButton
              label={t('agent_dashboard.user_detail.template.all_style_modal.cancel')}
              className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
              onClick={handleClose}
            />
            <TextButton
              label={t('agent_dashboard.user_detail.template.all_style_modal.save')}
              className='w-[135px] h-11'
              onClick={() => void handleSave()}
              disabled={!!saving || !selectedRowIds.length}
              loading={saving}
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );

  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
};
